<template>
  <teleport to="body">
    <div
      ref="overlay"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
      role="dialog"
      aria-modal="true"
      aria-label="Bildergalerie"
      tabindex="-1"
      @click.self="close"
      @keydown="onKeydown"
    >
      <!-- Schließen -->
      <button
        ref="closeButton"
        type="button"
        class="absolute top-4 right-4 z-10 flex h-12 w-12 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Schließen"
        @click="close"
      >
        <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- Zurück -->
      <button
        v-if="images.length > 1"
        type="button"
        class="absolute left-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:left-6"
        aria-label="Vorheriges Bild"
        @click="prev"
      >
        <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <!-- Bild + Bildunterschrift -->
      <figure class="pointer-events-none flex max-h-[90vh] max-w-[90vw] flex-col items-center" aria-live="polite">
        <img
          v-if="currentImage"
          :key="index"
          :src="currentImage.src"
          :alt="currentImage.caption || ''"
          class="max-h-[80vh] max-w-[90vw] object-contain"
        />
        <figcaption v-if="currentImage && currentImage.caption" class="mt-3 max-w-[80vw] text-center text-sm text-white/90">
          {{ currentImage.caption }}
        </figcaption>
        <span v-if="images.length > 1" class="mt-2 text-xs text-white/60">
          {{ index + 1 }} / {{ images.length }}
        </span>
      </figure>

      <!-- Weiter -->
      <button
        v-if="images.length > 1"
        type="button"
        class="absolute right-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-6"
        aria-label="Nächstes Bild"
        @click="next"
      >
        <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </teleport>
</template>

<script>
// Abhängigkeitsfreie Fullscreen-Overlay-Galerie. Wird später programmatisch
// vom Lightbox-Enhancer instanziiert (Links mit data-lightbox-group).
// Schließen über Escape, Klick auf den Hintergrund oder den Button;
// der Fokus wird beim Öffnen eingefangen und beim Schließen zurückgegeben.
export default {
  name: 'Lightbox',
  props: {
    // Array von { src, caption }
    images: { type: Array, required: true },
    startIndex: { type: Number, default: 0 },
  },
  emits: ['close'],
  data() {
    return {
      index: Math.min(Math.max(this.startIndex, 0), Math.max(this.images.length - 1, 0)),
      previouslyFocused: null,
    }
  },
  computed: {
    currentImage() {
      return this.images[this.index] || null
    },
  },
  mounted() {
    this.previouslyFocused = document.activeElement
    document.documentElement.classList.add('overflow-hidden')
    this.$nextTick(() => {
      if (this.$refs.closeButton) this.$refs.closeButton.focus()
    })
  },
  beforeUnmount() {
    document.documentElement.classList.remove('overflow-hidden')
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus()
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    prev() {
      this.index = (this.index - 1 + this.images.length) % this.images.length
    },
    next() {
      this.index = (this.index + 1) % this.images.length
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.close()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.prev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.next()
      } else if (event.key === 'Tab') {
        // Fokus im Overlay halten
        const focusable = this.$refs.overlay.querySelectorAll('button')
        if (!focusable.length) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    },
  },
}
</script>
